import { useState } from 'react';
import { useApp } from '../context/AppContext.jsx';
import { api } from '../lib/api.js';
import { copyText } from '../lib/util.js';
import { Modal } from '../components/Modal.jsx';
import { Btn, Field, MonoInput, PageHeading } from '../components/ui.jsx';

const BROKEN_PLACEHOLDER = (
  <div className="flex h-full w-full items-center justify-center bg-void p-6 text-center font-mono text-[10px] uppercase tracking-widest text-dim">
    Image unavailable
  </div>
);

export default function Lifeinvader() {
  const { snapshot, token, pushToast, refresh } = useApp();
  const isManager = Boolean(token);
  const ads = snapshot.ads || [];

  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ title: '', image_url: '' });
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(() => new Set());

  const hostname = (u) => {
    try {
      return new URL(u).hostname.replace(/^www\./, '');
    } catch {
      return '';
    }
  };

  const handleCopy = async (title, url) => {
    const ok = await copyText(`${title}\n${url}`);
    pushToast(ok ? 'Copied!' : 'Copy failed', ok ? 'teal' : 'ember');
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteAd(id, token);
      pushToast('Ad removed');
      refresh();
    } catch (err) {
      pushToast(err.message, 'ember');
    }
  };

  const handleAdd = async (event) => {
    event.preventDefault();
    if (!form.title.trim() || !form.image_url.trim() || busy) return;
    setBusy(true);
    try {
      await api.addAd({ title: form.title.trim(), image_url: form.image_url.trim() }, token);
      pushToast('Ad added');
      setForm({ title: '', image_url: '' });
      setAddOpen(false);
      refresh();
    } catch (err) {
      pushToast(err.message, 'ember');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeading
        eyebrow="Ad Gallery"
        title="Lifeinvader Ads"
        live
        actions={
          <>
            <span className="font-mono text-[10px] uppercase tracking-widest text-dim">
              {ads.length} ad{ads.length === 1 ? '' : 's'}
            </span>
            {isManager && (
              <Btn variant="teal" onClick={() => setAddOpen(true)}>
                + Add Ad
              </Btn>
            )}
          </>
        }
      />

      {ads.length === 0 ? (
        <div className="border border-dashed border-raise bg-surface/50 p-10 text-center font-mono text-[11px] uppercase tracking-widest text-dim">
          No ads yet{isManager ? ' — add the first one' : ''}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {ads.map((ad) => {
            const isBroken = broken.has(ad.id);
            return (
              <figure key={ad.id} className="flex flex-col border border-edge bg-panel">
                <div className="aspect-[4/3] overflow-hidden border-b border-edge bg-coal">
                  {isBroken ? (
                    BROKEN_PLACEHOLDER
                  ) : (
                    <img
                      src={ad.image_url}
                      alt={ad.title}
                      loading="lazy"
                      onError={() => setBroken((s) => new Set(s).add(ad.id))}
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
                <figcaption className="flex min-w-0 flex-1 flex-col gap-2 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate font-mono text-xs font-bold uppercase tracking-widest text-snow">
                      {ad.title}
                    </p>
                    {isManager && (
                      <button
                        onClick={() => handleDelete(ad.id)}
                        title="Remove ad"
                        className="grid h-7 w-7 shrink-0 place-items-center border border-ember/60 font-mono text-xs text-ember transition-colors hover:bg-ember/10"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  <p className="truncate font-mono text-[10px] text-dim" title={ad.image_url}>
                    {hostname(ad.image_url) || ad.image_url}
                  </p>
                  <Btn
                    size="sm"
                    variant="teal"
                    className="mt-auto w-full"
                    onClick={() => handleCopy(ad.title, ad.image_url)}
                  >
                    Copy Link
                  </Btn>
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}

      {isManager && (
        <Modal
          open={addOpen}
          onClose={() => setAddOpen(false)}
          title="New Lifeinvader Ad"
          accent="teal"
          footer={
            <>
              <Btn variant="ghost" onClick={() => setAddOpen(false)}>
                Cancel
              </Btn>
              <Btn
                type="submit"
                form="new-ad-form"
                variant="teal"
                disabled={!form.title.trim() || !form.image_url.trim() || busy}
              >
                {busy ? 'Saving…' : 'Add Ad'}
              </Btn>
            </>
          }
        >
          <form id="new-ad-form" onSubmit={handleAdd} className="space-y-4">
            <Field label="Title">
              <MonoInput
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. Weekend Stock Drop"
                maxLength={60}
              />
            </Field>
            <Field
              label="Image URL"
              hint="Direct link to a PNG / JPG / GIF — Gyazo (gyazo.com) or i.ibb.co recommended."
            >
              <MonoInput
                value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://i.ibb.co/xxxx/image.png"
              />
            </Field>
            {form.image_url.trim() && (
              <div className="border border-edge bg-coal p-2">
                <img
                  src={form.image_url.trim()}
                  alt="Preview"
                  className="mx-auto max-h-48 object-contain"
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden';
                  }}
                  onLoad={(e) => {
                    e.currentTarget.style.visibility = 'visible';
                  }}
                />
              </div>
            )}
          </form>
        </Modal>
      )}
    </div>
  );
}